<template>
  <nav class="navbar navbar-expand navbar-light navbar-bg">
    <a class="sidebar-toggle d-flex p-2" @click.prevent="hideLeftMenu">
      <i class="hamburger align-self-center"></i>
    </a>
    <div class="navbar-collapse collapse">
      <ul class="navbar-nav navbar-align">
        <!-- <li class="nav-item dropdown">
          <notification-dropdown></notification-dropdown>
        </li> -->
        <!-- <li class="nav-item dropdown">
          <message-dropdown></message-dropdown>
        </li> -->
        <li
          v-if="sessionExpiresAt && !sessionExpired"
          class="nav-item d-flex align-items-center h-100 px-3 session-countdown"
        >
          <span
            class="session-countdown-text"
            :class="{ 'session-countdown-text--soon': isExpiringSoon }"
          >
            Session expires in {{ formattedTimeLeft }}
          </span>
        </li>
        <li class="nav-item d-flex align-items-center h-100">
          <token-expiration-warning
            v-if="tokenExpirationWarning.show"
            :time-left="tokenExpirationWarning.timeLeft"
            @refresh-session="handleRefreshSession"
            class="h-100 d-flex align-items-center"
          />
        </li>
        <li class="nav-item dropdown">
          <user-dropdown></user-dropdown>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
// import NotificationDropDown from "@/components/notification-dropdown.vue";
// import MessageDropDown from "@/components/message-dropdown.vue";
import UserDropDown from "@/components/user-dropdown.vue";
import TokenExpirationWarning from "@/components/token-expiration-warning.vue";
import ToastMixin from "@/mixin/toast-mixin";
import { eventBus } from "@/services/event-bus";
import { mapState, mapActions } from "vuex";

export default {
  mixins: [ToastMixin],
  data: function() {
    return {
      // Absolute session expiry (ms epoch) decoded from the Auth0 token `exp`.
      sessionExpiresAt: null,
      // Remaining time until expiry (ms), recomputed every second.
      remainingMs: 0,
      // Guards against firing the expiry handler / toast more than once.
      sessionExpired: false,
      sessionCountdownInterval: null
    };
  },
  components: {
    // "notification-dropdown": NotificationDropDown,
    // "message-dropdown": MessageDropDown,
    "user-dropdown": UserDropDown,
    "token-expiration-warning": TokenExpirationWarning
  },
  computed: {
    ...mapState(["tokenExpirationWarning"]),
    formattedTimeLeft() {
      const totalSeconds = Math.max(0, Math.floor(this.remainingMs / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const pad = n => n.toString().padStart(2, "0");
      return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`;
    },
    isExpiringSoon() {
      // Highlight the countdown in the final minute.
      return this.remainingMs > 0 && this.remainingMs <= 60000;
    }
  },
  methods: {
    ...mapActions(["refreshToken"]),
    hideLeftMenu() {
      eventBus.emit("toggleLeftMenuEvent");
    },
    async handleRefreshSession() {
      try {
        // Call the refreshToken action and get the new token
        const result = await this.refreshToken({
          oktaAuth: this.$auth.oktaAuth
        });

        // Get the new expiration time from the refreshed token
        const tokenExpiration = await this.$auth.oktaAuth.tokenManager.get(
          "accessToken"
        );
        const newExpirationTime = tokenExpiration.expiresAt * 1000 - Date.now();

        // Return the new expiration time to update the warning component
        return { newExpirationTime };
      } catch (error) {
        console.error("Failed to refresh token:", error);
        throw error;
      }
    },
    async initSessionCountdown() {
      try {
        // Prefer the token already in the store; fall back to Auth0 for the
        // case where Vuex was reset (e.g. a hard refresh of a /secure page).
        let idToken = this.$store.getters.idToken;
        if (!idToken) {
          const tokens = await this.$auth.getTokens();
          idToken = tokens && tokens.idToken;
        }

        const exp = this.decodeTokenExp(idToken);
        if (!exp) {
          // Expiry unknown — leave the countdown hidden rather than guess.
          return;
        }

        this.sessionExpiresAt = exp * 1000;
        this.updateRemaining();
        if (this.remainingMs <= 0) {
          this.handleSessionExpired();
          return;
        }
        this.sessionCountdownInterval = setInterval(
          this.tickSessionCountdown,
          1000
        );
      } catch (error) {
        // getTokens() already clears storage and redirects on a dead session.
        console.error("Failed to initialize session countdown:", error);
      }
    },
    tickSessionCountdown() {
      this.updateRemaining();
      if (this.remainingMs <= 0) {
        this.handleSessionExpired();
      }
    },
    updateRemaining() {
      this.remainingMs = Math.max(0, this.sessionExpiresAt - Date.now());
    },
    async handleSessionExpired() {
      if (this.sessionExpired) return;
      this.sessionExpired = true;
      this.clearSessionCountdown();

      this.displayMessage(
        "warning",
        "Your session has expired. Redirecting to login…"
      );

      // Clear auth state (also stops the store's token-expiration check).
      await this.$store.dispatch("logout");

      // Brief pause so the toast is visible before the route changes.
      setTimeout(() => {
        if (this.$route.path !== "/login") {
          this.$router.push("/login");
        }
      }, 1500);
    },
    decodeTokenExp(token) {
      if (!token || typeof token !== "string") return null;
      try {
        const payload = token.split(".")[1];
        if (!payload) return null;
        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const claims = JSON.parse(atob(normalized));
        return typeof claims.exp === "number" ? claims.exp : null;
      } catch (error) {
        console.error("Failed to decode token expiration:", error);
        return null;
      }
    },
    clearSessionCountdown() {
      if (this.sessionCountdownInterval) {
        clearInterval(this.sessionCountdownInterval);
        this.sessionCountdownInterval = null;
      }
    }
  },
  mounted() {
    this.initSessionCountdown();
  },
  beforeDestroy() {
    this.clearSessionCountdown();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/navbar.scss";
@import "@/assets/scss/avatar.scss";
@import "@/assets/scss/hamburger.scss";

.navbar {
  height: 64px;

  @media (min-width: 1px) and (max-width: #{map-get($grid-breakpoints, 'lg') - .02px}) {
    position: sticky;
    top: 0;
    z-index: 1030;
  }
}

.nav-item {
  height: 100%;
}

.session-countdown-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  white-space: nowrap;
}

.session-countdown-text--soon {
  color: #dc2626;
  font-weight: 600;
}
</style>
